import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { getCustomerByCategoryReportService } from "../../../services/report/services";
import { useTranslator } from "../../../context/translator";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const CustomerCategoryReport = () => {
  const { user } = useSelector((state) => state.user);
  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );
  const { translate } = useTranslator();
  const [data, setData] = useState([]);
  const [textColor, setTextColor] = useState("black");

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)"); //
    setTextColor(mediaQuery.matches ? "white" : "black");

    const handleChange = (e) => {
      setTextColor(e.matches ? "white" : "black");
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const categoryOptions = [
    { key: "hl", name: translate("health") },
    { key: "nt", name: translate("nutrition") },
    { key: "tr", name: translate("tourism") },
    { key: "wl", name: translate("welfare") },
    { key: "tnr", name: translate("transport") },
    { key: "sh", name: translate("shop") },
    { key: "ent", name: translate("entertainment") },
    { key: "edc", name: translate("education") },
    { key: "pts", name: translate("pets") },
  ];

  const getData = useCallback(async () => {
    const result = await getCustomerByCategoryReportService({
      companyId: company?._id,
    });

    setData(result || []);
  }, [company]);

  useEffect(() => {
    getData();
  }, [getData]);

  const labels = categoryOptions.map((opt) => opt.name);
  const dataMap = Object.fromEntries(data.map((d) => [d.category, d.count]));

  const counts = categoryOptions.map((opt) => dataMap[opt.key] || 0);

  const backgroundColors = [
    "rgba(255, 99, 132, 0.5)", // health
    "rgba(54, 162, 235, 0.5)", // nutrition
    "rgba(255, 206, 86, 0.5)", // tourism
    "rgba(75, 192, 192, 0.5)", // welfare
    "rgba(153, 102, 255, 0.5)", // transport
    "rgba(255, 159, 64, 0.5)", // shop
    "rgba(201, 203, 207, 0.5)", // entertainment
    "rgba(100, 149, 237, 0.5)", // education
    "rgba(60, 179, 113, 0.5)", // pets
  ];

  const chartData = {
    labels,
    datasets: [
      {
        label: translate("customers-by-category"),
        data: counts,
        backgroundColor: backgroundColors,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
        labels: {
          color: textColor,
        },
      },
      title: {
        display: true,
        text: translate("customers-by-category"),
        color: textColor,
      },
    },
    scales: {
      x: {
        ticks: {
          color: textColor,
        },
        grid: {
          color:
            textColor === "white"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.1)",
        },
      },
      y: {
        ticks: {
          color: textColor,
        },
        grid: {
          color:
            textColor === "white"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.1)",
        },
      },
    },
  };

  const handleGeneratePDF = () => {
    const doc = new jsPDF();
    doc.text(translate("customers-by-category-report"), 14, 15);

    autoTable(doc, {
      startY: 20,
      head: [[translate("category"), translate("customers-by-category")]],
      body: categoryOptions.map((opt) => [opt.name, dataMap[opt.key] || 0]),
    });

    doc.save("customers-by-category-report.pdf");
  };

  if (userLoading || companyLoading) return null;

  return (
    <div className="p-4">
      <div className="flex justify-between">
        <h2 className="text-xl font-semibold mb-4  dark:text-white 600">
          {translate("customers-by-category-report")}
        </h2>
        <button
          onClick={handleGeneratePDF}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          {translate("download-PDF")}
        </button>
      </div>

      <div className="mb-4">
        <Bar data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};

export default CustomerCategoryReport;
